/**
 * `YYYY-MM-DD`, with the month and day ranges enforced.
 *
 * Shape alone is not enough. `toIsoDate` in `mapEdi845ToSpa` builds these by
 * slicing any 8 digits, so a supplier typo reaches `rawData` as "2026-13-45" —
 * which a `\d{2}` pattern accepts and a `DATEONLY` column rejects, failing a
 * whole backfill chunk on one bad line. ISO date-only strings still sort
 * lexicographically, so min/max need no parsing.
 */
const DATE_ONLY_PATTERN = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

/**
 * Year 0000 passes every check above and round-trips through `Date`, which
 * permits it — but Postgres has no year zero and rejects `0000-01-01` as out
 * of range, so it would fail a `DATEONLY` write the same way "2026-13-45"
 * would. `DTM 092` of `00000101` is all it takes.
 */
const MIN_YEAR = "0001";

/**
 * One SPA line, as far as its window is concerned.
 *
 * Typed loosely because the only place these dates survive on a persisted
 * record is `SpaLineItem.rawData`, a verbatim bag of the vendor's own columns:
 * the keys are present on 845-sourced lines and absent on CSV-sourced ones, and
 * nothing validates what they hold.
 */
export interface SpaLineWindowSource {
  effectiveStart?: unknown;
  effectiveEnd?: unknown;
}

export interface SpaWindow {
  /** Earliest start across the dated lines, or null when none carry one. */
  startDate: string | null;
  /** Latest end across the dated lines, or null when none carry one. */
  endDate: string | null;
  /**
   * True when the lines do not all sit in one window, so `startDate`/`endDate`
   * is a span covering several rather than the one date pair the agreement
   * states. What the UI needs in order to avoid presenting a span as a single
   * effective window.
   */
  isRange: boolean;
  /** Distinct `(start, end)` pairs across the dated lines, half-open included. */
  distinctWindowCount: number;
}

function asDateOnly(value: unknown): string | null {
  if (typeof value !== "string" || !DATE_ONLY_PATTERN.test(value)) return null;
  if (value.slice(0, 4) < MIN_YEAR) return null;
  // The pattern still admits a day the month does not have (2026-02-31), which
  // `Date` normalises rather than rejecting — so compare the round trip.
  return new Date(`${value}T00:00:00Z`).toISOString().startsWith(value)
    ? value
    : null;
}

/**
 * The window a SPA's line items describe.
 *
 * Suppliers differ in where they state an agreement's dates. Rockwell puts them
 * in the 845 header; Schneider states them only inside each `PAD` loop, so the
 * header carries no window at all and the lines are the only record of one.
 *
 * Lines carrying no date are skipped rather than counted: a blank row is
 * silence, not a second window, and treating it as one would label an
 * otherwise-unanimous agreement a range.
 *
 * A line stating only ONE of the two dates is different, and does count as its
 * own window. It has to: its date widens the span, so excluding it from the
 * count would let the result report a single agreed window while the dates
 * shown are wider than any window a line actually states — a March–June line
 * plus a January start-only line reading "January–June, all lines agree",
 * which no line said. Whatever the count means, it has to describe the same
 * lines the span came from.
 *
 * Neither case is observed: across the 6,947-document corpus (804,912 lines)
 * exactly zero carry one of `DTM 092`/`093` without the other. Both are
 * handled because `rawData` is a verbatim bag that any future channel can fill
 * differently.
 */
export function deriveSpaWindow(lines: SpaLineWindowSource[]): SpaWindow {
  const windows = new Set<string>();
  let startDate: string | null = null;
  let endDate: string | null = null;

  for (const line of lines) {
    const start = asDateOnly(line.effectiveStart);
    const end = asDateOnly(line.effectiveEnd);
    if (start === null && end === null) continue;

    windows.add(`${start ?? ""}|${end ?? ""}`);
    if (start !== null && (startDate === null || start < startDate))
      startDate = start;
    if (end !== null && (endDate === null || end > endDate)) endDate = end;
  }

  return {
    startDate,
    endDate,
    isRange: windows.size > 1,
    distinctWindowCount: windows.size,
  };
}
